/**
 * Admin Project Detail Page
 *
 * Shows project details with tabbed navigation for:
 * - Details (core settings)
 * - Configuration (synced .cortex/config.json)
 * - Connections (linked provider connections)
 */

import { Card, Center, Stack, Text } from '@cortex/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import {
  EnvironmentVariablesTab,
  ProjectConfigurationTab,
  ProjectConnectionsTab,
  ProjectDetailsTab,
  ProjectHeader,
  ProjectTabs,
} from '@/components/admin';
import { useFetchWithAuth } from '@/hooks/use-api';

const projectSearchSchema = z.object({
  tab: z.string().optional(),
});

export const Route = createFileRoute('/_app/admin/projects/$id')({
  validateSearch: projectSearchSchema,
  component: ProjectDetailPage,
});

interface Project {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  repo_url: string | null;
  default_branch: string;
  organization_id: string;
  config: Record<string, unknown>;
  config_raw: Record<string, unknown> | null;
  config_sync_status: string;
  config_synced_at: string | null;
  config_sync_error: string | null;
  config_file_sha: string | null;
  created_at: string;
  updated_at: string;
  organizations?: {
    id: string;
    name: string;
    slug: string;
  };
}

function ProjectDetailPage() {
  const { id: projectId } = Route.useParams();
  const { tab } = Route.useSearch();
  const fetchWithAuth = useFetchWithAuth();
  const activeTab = tab || 'details';
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch project data by ID
  useEffect(() => {
    const fetchProject = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetchWithAuth(`/admin/projects/${projectId}`);
        if (!response.ok) {
          throw new Error('Failed to fetch project');
        }

        const { data } = await response.json();
        setProject(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load project');
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [projectId, fetchWithAuth]);

  // Refresh project data
  const refreshProject = async () => {
    try {
      const response = await fetchWithAuth(`/admin/projects/${projectId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch project');
      }

      const { data } = await response.json();
      setProject(data);
    } catch {
      // Silently handle fetch failure - UI shows stale data
    }
  };

  if (loading) {
    return (
      <Center minHeight="24rem">
        <Text color="muted">Loading...</Text>
      </Center>
    );
  }

  if (error || !project) {
    return (
      <Center minHeight="24rem">
        <Stack gap="md" align="center">
          <Text color="error">{error || 'Project not found'}</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="lg" className="p-6">
      {/* Header with name and back link */}
      <ProjectHeader project={project} />

      {/* Tab navigation */}
      <ProjectTabs projectId={project.id} activeTab={activeTab} />

      {/* Tab content */}
      <Card className="p-6">
        {activeTab === 'details' && (
          <ProjectDetailsTab project={project} onUpdate={refreshProject} />
        )}

        {activeTab === 'configuration' && (
          <ProjectConfigurationTab
            project={project}
            onUpdate={refreshProject}
          />
        )}

        {activeTab === 'connections' && (
          <ProjectConnectionsTab
            projectId={project.id}
            organizationId={project.organization_id}
          />
        )}

        {activeTab === 'environment' && (
          <EnvironmentVariablesTab
            organizationId={project.organization_id}
            projectId={project.id}
          />
        )}
      </Card>
    </Stack>
  );
}
